import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import { roleAPI, permissionAPI, userRoleAPI, rbacUtils } from '../services/rbacApi';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  roles: [],
  permissions: [],
  roleHierarchy: null,
  userRoles: {},
  selectedRole: null,
  selectedPermission: null,
  loading: {
    roles: false,
    permissions: false,
    hierarchy: false,
    assignment: false
  },
  error: null,
  filters: {
    level: null,
    isActive: true,
    permissionModule: null,
    permissionAction: null,
    permissionCategory: null,
    search: ''
  },
  pagination: {
    roles: { current: 1, limit: 20, total: 0, pages: 0 },
    permissions: { current: 1, limit: 20, total: 0, pages: 0 }
  }
};

// Action types
const RBAC_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Roles
  SET_ROLES: 'SET_ROLES',
  ADD_ROLE: 'ADD_ROLE',
  UPDATE_ROLE: 'UPDATE_ROLE',
  REMOVE_ROLE: 'REMOVE_ROLE',
  SET_SELECTED_ROLE: 'SET_SELECTED_ROLE',
  SET_ROLE_HIERARCHY: 'SET_ROLE_HIERARCHY',
  
  // Permissions
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  ADD_PERMISSION: 'ADD_PERMISSION',
  UPDATE_PERMISSION: 'UPDATE_PERMISSION',
  REMOVE_PERMISSION: 'REMOVE_PERMISSION',
  SET_SELECTED_PERMISSION: 'SET_SELECTED_PERMISSION',
  
  // User Roles
  SET_USER_ROLES: 'SET_USER_ROLES',
  ADD_USER_ROLE: 'ADD_USER_ROLE',
  REMOVE_USER_ROLE: 'REMOVE_USER_ROLE',
  
  // Filters and Pagination
  SET_FILTERS: 'SET_FILTERS',
  UPDATE_PAGINATION: 'UPDATE_PAGINATION'
};

// RBAC reducer
const rbacReducer = (state, action) => {
  switch (action.type) {
    case RBAC_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value
        }
      };

    case RBAC_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: {
          roles: false,
          permissions: false,
          hierarchy: false,
          assignment: false
        }
      };

    case RBAC_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case RBAC_ACTIONS.SET_ROLES: {
      const incoming = action.payload.pagination;
      const prev = state.pagination.roles;
      return {
        ...state,
        roles: action.payload.roles,
        pagination: {
          ...state.pagination,
          roles: incoming
            ? {
                ...prev,
                // Never let API page/limit rewrite wipe controlled pagination or re-trigger fetches
                total: Number(incoming.total ?? prev.total) || 0,
                pages: Number(incoming.pages ?? prev.pages) || 0,
              }
            : prev
        }
      };
    }

    case RBAC_ACTIONS.ADD_ROLE:
      return {
        ...state,
        roles: [...state.roles, action.payload]
      };

    case RBAC_ACTIONS.UPDATE_ROLE:
      return {
        ...state,
        roles: state.roles.map(role =>
          role._id === action.payload._id ? action.payload : role
        ),
        selectedRole: state.selectedRole?._id === action.payload._id ? action.payload : state.selectedRole
      };

    case RBAC_ACTIONS.REMOVE_ROLE:
      return {
        ...state,
        roles: state.roles.filter(role => role._id !== action.payload),
        selectedRole: state.selectedRole?._id === action.payload ? null : state.selectedRole
      };

    case RBAC_ACTIONS.SET_SELECTED_ROLE:
      return {
        ...state,
        selectedRole: action.payload
      };

    case RBAC_ACTIONS.SET_ROLE_HIERARCHY:
      return {
        ...state,
        roleHierarchy: action.payload
      };

    case RBAC_ACTIONS.SET_PERMISSIONS: {
      const incoming = action.payload.pagination;
      const prev = state.pagination.permissions;
      return {
        ...state,
        permissions: action.payload.permissions,
        pagination: {
          ...state.pagination,
          permissions: incoming
            ? {
                ...prev,
                total: Number(incoming.total ?? prev.total) || 0,
                pages: Number(incoming.pages ?? prev.pages) || 0,
              }
            : prev
        }
      };
    }

    case RBAC_ACTIONS.ADD_PERMISSION:
      return {
        ...state,
        permissions: [...state.permissions, action.payload]
      };

    case RBAC_ACTIONS.UPDATE_PERMISSION:
      return {
        ...state,
        permissions: state.permissions.map(permission =>
          permission._id === action.payload._id ? action.payload : permission
        ),
        selectedPermission: state.selectedPermission?._id === action.payload._id ? action.payload : state.selectedPermission
      };

    case RBAC_ACTIONS.REMOVE_PERMISSION:
      return {
        ...state,
        permissions: state.permissions.filter(permission => permission._id !== action.payload),
        selectedPermission: state.selectedPermission?._id === action.payload ? null : state.selectedPermission
      };

    case RBAC_ACTIONS.SET_SELECTED_PERMISSION:
      return {
        ...state,
        selectedPermission: action.payload
      };

    case RBAC_ACTIONS.SET_USER_ROLES:
      return {
        ...state,
        userRoles: {
          ...state.userRoles,
          [action.payload.userId]: action.payload.roles
        }
      };

    case RBAC_ACTIONS.ADD_USER_ROLE:
      return {
        ...state,
        userRoles: {
          ...state.userRoles,
          [action.payload.userId]: [
            ...(state.userRoles[action.payload.userId] || []),
            action.payload.role
          ]
        }
      };

    case RBAC_ACTIONS.REMOVE_USER_ROLE:
      return {
        ...state,
        userRoles: {
          ...state.userRoles,
          [action.payload.userId]: (state.userRoles[action.payload.userId] || [])
            .filter(role => role.code !== action.payload.roleCode)
        }
      };

    case RBAC_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };

    case RBAC_ACTIONS.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.type]: {
            ...state.pagination[action.payload.type],
            ...action.payload.data
          }
        }
      };

    default:
      return state;
  }
};

// Create RBAC context
const RBACContext = createContext({
  roles: [], permissions: [], userRoles: [], selectedRole: null, selectedPermission: null,
  isLoading: true, error: null, filters: {}, pagination: {},
  fetchRoles: async () => {}, fetchRoleHierarchy: async () => {}, createRole: async () => {},
  updateRole: async () => {}, deleteRole: async () => {}, selectRole: () => {},
  fetchPermissions: async () => {}, createPermission: async () => {},
  updatePermission: async () => {}, deletePermission: async () => {}, syncPermissions: async () => {}, selectPermission: () => {},
  bulkAssignPermissions: async () => {}, bulkRevokePermissions: async () => {},
  fetchUserRoles: async () => {}, assignUserRole: async () => {}, revokeUserRole: async () => {},
  setFilters: () => {}, updatePagination: () => {},
});

// RBAC Provider component
export const RBACProvider = ({ children }) => {
  const [state, dispatch] = useReducer(rbacReducer, initialState);

  // Utility functions
  const setLoading = useCallback((key, value) => {
    dispatch({
      type: RBAC_ACTIONS.SET_LOADING,
      payload: { key, value }
    });
  }, []);

  const setError = useCallback((error) => {
    dispatch({
      type: RBAC_ACTIONS.SET_ERROR,
      payload: error
    });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: RBAC_ACTIONS.CLEAR_ERROR });
  }, []);

  // Role management functions
  const rolesFetchAbortRef = useRef(null);
  const permissionsFetchAbortRef = useRef(null);

  const fetchRoles = useCallback(async (params = {}) => {
    if (rolesFetchAbortRef.current) {
      rolesFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    rolesFetchAbortRef.current = controller;

    setLoading('roles', true);
    clearError();

    try {
      // Only send role-list filters — shared RBAC filters also hold user/permission keys
      const { search, level, isActive } = state.filters;
      const response = await roleAPI.getRoles({
        ...(search ? { search } : {}),
        ...(level != null && level !== '' ? { level } : {}),
        ...(isActive !== undefined && isActive !== null ? { isActive } : {}),
        page: state.pagination.roles.current,
        limit: state.pagination.roles.limit,
        // List UI only needs permissionCount — avoid shipping full permission trees
        includePermissions: false,
        includePermissionCounts: true,
        includeUserCounts: true,
        ...params,
      }, { signal: controller.signal });

      const paginationMeta = response.data?.data?.pagination || {};
      dispatch({
        type: RBAC_ACTIONS.SET_ROLES,
        payload: {
          roles: response.data.data.roles,
          pagination: {
            total: paginationMeta.total ?? 0,
            pages: paginationMeta.pages ?? 0,
          }
        }
      });
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      setError(error.response?.data?.message || 'Failed to fetch roles');
      toast.error('Failed to load roles');
    } finally {
      if (rolesFetchAbortRef.current === controller) {
        setLoading('roles', false);
      }
    }
  }, [state.filters.search, state.filters.level, state.filters.isActive, state.pagination.roles.current, state.pagination.roles.limit, clearError, setLoading, setError]);

  const fetchRoleHierarchy = useCallback(async () => {
    setLoading('hierarchy', true);
    
    try {
      const response = await roleAPI.getRoleHierarchy();
      dispatch({
        type: RBAC_ACTIONS.SET_ROLE_HIERARCHY,
        payload: response.data.data.hierarchy
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch role hierarchy');
      toast.error('Failed to load role hierarchy');
    } finally {
      setLoading('hierarchy', false);
    }
  }, []);

  const createRole = useCallback(async (roleData) => {
    setLoading('roles', true);
    
    try {
      const response = await roleAPI.createRole(roleData);
      dispatch({
        type: RBAC_ACTIONS.ADD_ROLE,
        payload: response.data.data.role
      });
      toast.success('Role created successfully');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create role';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('roles', false);
    }
  }, []);

  const updateRole = useCallback(async (identifier, roleData) => {
    setLoading('roles', true);
    
    try {
      const response = await roleAPI.updateRole(identifier, roleData);
      dispatch({
        type: RBAC_ACTIONS.UPDATE_ROLE,
        payload: response.data.data.role
      });
      toast.success('Role updated successfully');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update role';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('roles', false);
    }
  }, []);

  const deleteRole = useCallback(async (identifier, force = false) => {
    setLoading('roles', true);
    
    try {
      await roleAPI.deleteRole(identifier, force);
      dispatch({
        type: RBAC_ACTIONS.REMOVE_ROLE,
        payload: identifier
      });
      toast.success('Role deleted successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete role';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('roles', false);
    }
  }, []);

  const selectRole = useCallback((role) => {
    dispatch({
      type: RBAC_ACTIONS.SET_SELECTED_ROLE,
      payload: role
    });
  }, []);

  // Permission management functions
  const fetchPermissions = useCallback(async (params = {}) => {
    if (permissionsFetchAbortRef.current) {
      permissionsFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    permissionsFetchAbortRef.current = controller;

    setLoading('permissions', true);
    clearError();

    try {
      const { search, isActive, permissionModule, permissionAction, permissionCategory } = state.filters;
      const queryParams = {
        page: state.pagination.permissions.current,
        limit: state.pagination.permissions.limit,
        includeStats: false,
        includeModuleStats: false,
        ...(search ? { search } : {}),
        ...(permissionModule ? { module: permissionModule } : {}),
        ...(permissionAction ? { action: permissionAction } : {}),
        ...(permissionCategory ? { category: permissionCategory } : {}),
        ...(isActive !== undefined && isActive !== null ? { isActive: String(isActive) } : {}),
        ...params,
      };

      const response = await permissionAPI.getPermissions(queryParams, { signal: controller.signal });
      const paginationMeta = response.data?.data?.pagination || {};

      dispatch({
        type: RBAC_ACTIONS.SET_PERMISSIONS,
        payload: {
          permissions: response.data.data.permissions,
          pagination: {
            total: paginationMeta.total ?? 0,
            pages: paginationMeta.pages ?? 0,
          }
        }
      });
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      setError(error.response?.data?.message || 'Failed to fetch permissions');
      toast.error('Failed to load permissions');
    } finally {
      if (permissionsFetchAbortRef.current === controller) {
        setLoading('permissions', false);
      }
    }
  }, [
    state.filters.search,
    state.filters.isActive,
    state.filters.permissionModule,
    state.filters.permissionAction,
    state.filters.permissionCategory,
    state.pagination.permissions.current,
    state.pagination.permissions.limit,
    clearError,
    setLoading,
    setError,
  ]);

  const createPermission = useCallback(async (permissionData) => {
    setLoading('permissions', true);
    
    try {
      const response = await permissionAPI.createPermission(permissionData);
      dispatch({
        type: RBAC_ACTIONS.ADD_PERMISSION,
        payload: response.data.data.permission
      });
      toast.success('Permission created successfully');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create permission';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('permissions', false);
    }
  }, []);

  const updatePermission = useCallback(async (identifier, permissionData) => {
    setLoading('permissions', true);
    
    try {
      const response = await permissionAPI.updatePermission(identifier, permissionData);
      dispatch({
        type: RBAC_ACTIONS.UPDATE_PERMISSION,
        payload: response.data.data.permission
      });
      toast.success('Permission updated successfully');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update permission';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('permissions', false);
    }
  }, []);

  const deletePermission = useCallback(async (identifier, force = false) => {
    setLoading('permissions', true);
    
    try {
      await permissionAPI.deletePermission(identifier, force);
      dispatch({
        type: RBAC_ACTIONS.REMOVE_PERMISSION,
        payload: identifier
      });
      toast.success('Permission deleted successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete permission';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('permissions', false);
    }
  }, []);

  const syncPermissions = useCallback(async () => {
    setLoading('permissions', true);
    clearError();

    try {
      const response = await permissionAPI.syncPermissions();
      const initialJob = response.data.data?.job;

      if (!initialJob?.jobId) {
        throw new Error('Permission sync job was not created');
      }

      const pollIntervalMs = 2000;
      const maxPollAttempts = 300; // 10 minutes
      let latestJob = initialJob;
      let attempt = 0;

      while (attempt < maxPollAttempts) {
        if (latestJob.status === 'completed' || latestJob.status === 'failed') {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        const statusResponse = await permissionAPI.getPermissionSyncStatus(initialJob.jobId);
        latestJob = statusResponse.data.data?.job || latestJob;
        attempt += 1;
      }

      if (latestJob.status !== 'completed') {
        const syncError = latestJob.error?.message || 'Permission sync did not complete successfully';
        throw new Error(syncError);
      }

      const summary = latestJob.result?.summary || {};
      toast.success(
        `Permission sync complete: ${summary.created || 0} created, ${summary.updated || 0} updated, ${summary.deleted || 0} deleted`
      );

      return { success: true, data: latestJob.result, job: latestJob };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sync permissions';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('permissions', false);
    }
  }, [clearError, setError]);

  const selectPermission = useCallback((permission) => {
    dispatch({
      type: RBAC_ACTIONS.SET_SELECTED_PERMISSION,
      payload: permission
    });
  }, []);

  // Role-Permission assignment functions
  const bulkAssignPermissions = useCallback(async (roleCode, permissions, metadata = {}) => {
    setLoading('assignment', true);
    
    try {
      const response = await roleAPI.bulkAssignPermissions(roleCode, permissions, metadata);
      toast.success(`Successfully assigned ${permissions.length} permissions to role`);

      const updatedRole = response.data?.data?.role;
      if (updatedRole) {
        selectRole(updatedRole);
        dispatch({
          type: RBAC_ACTIONS.UPDATE_ROLE,
          payload: updatedRole,
        });
      } else if (state.selectedRole?.code === roleCode) {
        const refreshedRole = await roleAPI.getRole(roleCode);
        selectRole(refreshedRole.data.data.role);
      }
      
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to assign permissions';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('assignment', false);
    }
  }, [state.selectedRole?.code, selectRole, setError]);

  const bulkRevokePermissions = useCallback(async (roleCode, permissions, reason = '') => {
    setLoading('assignment', true);
    
    try {
      const response = await roleAPI.bulkRevokePermissions(roleCode, permissions, reason);
      toast.success(`Successfully revoked ${permissions.length} permissions from role`);

      const updatedRole = response.data?.data?.role;
      if (updatedRole) {
        selectRole(updatedRole);
        dispatch({
          type: RBAC_ACTIONS.UPDATE_ROLE,
          payload: updatedRole,
        });
      } else if (state.selectedRole?.code === roleCode) {
        const refreshedRole = await roleAPI.getRole(roleCode);
        selectRole(refreshedRole.data.data.role);
      }
      
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to revoke permissions';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('assignment', false);
    }
  }, [state.selectedRole?.code, selectRole, setError]);

  // User role management functions
  const fetchUserRoles = useCallback(async (userId) => {
    try {
      const response = await userRoleAPI.getUserRoles(userId);
      dispatch({
        type: RBAC_ACTIONS.SET_USER_ROLES,
        payload: {
          userId,
          roles: response.data.data.roles
        }
      });
      return response.data.data.roles;
    } catch (error) {
      toast.error('Failed to load user roles');
      return [];
    }
  }, []);

  const assignUserRole = useCallback(async (userId, roleCode, data = {}) => {
    setLoading('assignment', true);
    
    try {
      const response = await userRoleAPI.assignRole(userId, roleCode, data);
      dispatch({
        type: RBAC_ACTIONS.ADD_USER_ROLE,
        payload: {
          userId,
          role: response.data.data.role
        }
      });
      toast.success('Role assigned to user successfully');
      return { success: true, data: response.data.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to assign role to user';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('assignment', false);
    }
  }, []);

  const revokeUserRole = useCallback(async (userId, roleCode, reason = '') => {
    setLoading('assignment', true);
    
    try {
      await userRoleAPI.revokeRole(userId, roleCode, reason);
      dispatch({
        type: RBAC_ACTIONS.REMOVE_USER_ROLE,
        payload: { userId, roleCode }
      });
      toast.success('Role revoked from user successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to revoke role from user';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading('assignment', false);
    }
  }, []);

  // Filter and pagination functions
  const setFilters = useCallback((filters) => {
    dispatch({
      type: RBAC_ACTIONS.SET_FILTERS,
      payload: filters
    });
  }, []);

  const updatePagination = useCallback((type, data) => {
    dispatch({
      type: RBAC_ACTIONS.UPDATE_PAGINATION,
      payload: { type, data }
    });
  }, []);

  // Computed values
  const getAvailablePermissions = useCallback((roleCode) => {
    if (!state.selectedRole || state.selectedRole.code !== roleCode) {
      return state.permissions;
    }
    
    const assignedPermissions = state.selectedRole.permissions || [];
    const assignedCodes = assignedPermissions.map(p => p.code);
    
    return state.permissions.filter(p => !assignedCodes.includes(p.code));
  }, [state.selectedRole, state.permissions]);

  const getRolesByLevel = useCallback((level) => {
    return state.roles.filter(role => role.level === level);
  }, [state.roles]);

  const getPermissionsByModule = useCallback((module) => {
    return state.permissions.filter(p => p.module === module);
  }, [state.permissions]);

  const value = {
    // State
    ...state,
    
    // Role management
    fetchRoles,
    fetchRoleHierarchy,
    createRole,
    updateRole,
    deleteRole,
    selectRole,
    
    // Permission management
    fetchPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    syncPermissions,
    selectPermission,
    
    // Role-Permission assignments
    bulkAssignPermissions,
    bulkRevokePermissions,
    
    // User role management
    fetchUserRoles,
    assignUserRole,
    revokeUserRole,
    
    // Utilities
    setFilters,
    updatePagination,
    clearError,
    getAvailablePermissions,
    getRolesByLevel,
    getPermissionsByModule,
    
    // RBAC utilities
    rbacUtils
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

// Custom hook to use RBAC context
export const useRBAC = () => useContext(RBACContext);

export default RBACContext;